import { useTheme } from "@emotion/react";
import type { FC, RefObject } from "react";

import type { SupportSelectOption, SupportSelectVariant } from "./supportSelectTypes";

type Props<T extends string> = {
  listboxRef: RefObject<HTMLUListElement | null>;
  listboxId: string;
  optionIdPrefix: string;
  optionKeys: string[];
  options: SupportSelectOption<T>[];
  selectedIndex: number;
  activeIndex: number;
  variant: SupportSelectVariant;
  triggerBackground: string;
  onActiveIndexChange: (index: number) => void;
  onSelect: (index: number) => void;
};

export function SupportSelectListbox<T extends string>({
  listboxRef,
  listboxId,
  optionIdPrefix,
  optionKeys,
  options,
  selectedIndex,
  activeIndex,
  variant,
  triggerBackground,
  onActiveIndexChange,
  onSelect,
}: Props<T>) {
  const theme = useTheme();

  return (
    <ul
      ref={listboxRef}
      id={listboxId}
      role="listbox"
      css={{
        listStyle: "none",
        margin: 0,
        position: "absolute",
        top: "calc(100% + 4px)",
        left: 0,
        width: "100%",
        borderRadius: theme.smallBorderRadius,
        boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.2)",
        padding: "0.25rem",
        backgroundColor: triggerBackground,
        zIndex: 2,
        maxHeight: "16rem",
        overflowY: "auto",
      }}
    >
      {options.map((option, index) => {
        const isSelected = index === selectedIndex;
        const isActive = index === activeIndex;
        return (
          <li
            key={optionKeys[index]}
            id={`${optionIdPrefix}-${optionKeys[index]}`}
            role="option"
            aria-selected={isSelected}
            onMouseEnter={() => onActiveIndexChange(index)}
            onClick={() => onSelect(index)}
            css={{
              padding: "0.5rem 0.75rem",
              borderRadius: theme.extraSmallBorderRadius,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: isActive && variant === "form" ? "white" : option.color ?? theme.colors.default,
              fontWeight: 600,
              background: isActive
                ? variant === "form"
                  ? theme.colors.primary
                  : theme.colors.primaryLight
                : "transparent",
            }}
          >
            {option.icon}
            {option.label}
          </li>
        );
      })}
    </ul>
  );
}

type ColoredDotProps = {
  color: string;
};

export const SupportSelectDot: FC<ColoredDotProps> = ({ color }) => (
  <span
    css={{
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
    }}
  />
);
